import { useEffect } from 'react';
import PageLayout from '../../components/feature/PageLayout';
import PageHero from '../../components/feature/PageHero';

export default function FAQsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);

    // SEO Meta Tags
    document.title = 'Frequently Asked Questions | Evernorth AI | AI Training & Consulting FAQs';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Common questions about Evernorth AI workshops, consulting services, pricing, and AI readiness audits. Get answers about our AI training programs for Northwest Georgia businesses.');
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL}/faqs`);
    }

    // Schema.org JSON-LD - FAQPage is already in JSX markup below
    // No need to duplicate in head
  }, []);

  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      question: 'Is our team too small for AI?',
      answer: 'No. AI helps teams of any size. If your staff handles communication, documentation, or repetitive tasks, AI can save time immediately.'
    },
    {
      question: 'Do employees need technical experience?',
      answer: 'No. Everything we teach is designed for beginners. Every workflow we design is made with your team in mind.'
    },
    {
      question: 'We tried AI before and it didn\'t help. Why would this be different?',
      answer: 'Most teams fail because they skip training. We focus on fundamentals first.'
    },
    {
      question: 'Is this automation?',
      answer: 'Our company is about supporting people, not replacing them. After training your staff on how to use AI, we look forward to discussing with you how to automate parts of your work to make your office do more with less.'
    },
    {
      question: 'How long before we see value?',
      answer: 'Most teams see immediate improvement after training. We teach you "how to fish." Employees must practice what they have learned and implement it. Your team should start saving time the next day using what they\'ve learned.'
    },
    {
      question: 'Do you offer virtual training?',
      answer: 'Absolutely. We are here to support you how you need!'
    },
    {
      question: 'What if we are not ready?',
      answer: 'That is exactly what the short call is for. The Foundations Program is designed for those who don\'t think they are ready. We start at zero and no one gets left behind.'
    }
  ];

  return (
    <PageLayout>
      <PageHero
        badge="Common Questions"
        title="Frequently Asked"
        titleHighlight="Questions"
        description="Everything you need to know about our AI training and consulting services"
      />

      <section className="py-24 bg-white" itemScope itemType="https://schema.org/FAQPage">
        <div className="max-w-4xl mx-auto px-6">
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-[#f8f8f9] rounded-xl border border-soft-gray/30 overflow-hidden hover:border-gold/50 transition-all"
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
              >
                <button
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  className="w-full px-8 py-6 flex items-center justify-between text-left cursor-pointer hover:bg-soft-gray/10 transition-colors"
                >
                  <h3 className="text-lg font-semibold text-navy pr-8" itemProp="name">
                    {faq.question}
                  </h3>
                  <i
                    className={`ri-arrow-down-s-line text-2xl text-gold transition-transform duration-300 ${
                      openIndex === index ? 'rotate-180' : ''
                    }`}
                  ></i>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openIndex === index ? 'max-h-96' : 'max-h-0'
                  }`}
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <div className="px-8 pb-6">
                    <p className="text-charcoal leading-relaxed" itemProp="text">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <div className="inline-block bg-[#f8f8f9] p-8 rounded-2xl border border-soft-gray/30">
              <i className="ri-question-line text-5xl text-gold mb-4"></i>
              <h3 className="text-2xl font-bold mb-4 text-navy">Still Have Questions?</h3>
              <p className="text-charcoal mb-6 max-w-md mx-auto">
                We&apos;re here to help! Reach out to us and we&apos;ll answer any questions you have about AI training and implementation.
              </p>
              <Link
                to="/contact"
                className="inline-block px-8 py-3 bg-gold text-white font-semibold rounded-full hover:bg-[#a68a4e] transition-all cursor-pointer whitespace-nowrap"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
